import { ChevronDown } from 'lucide-react';

import {
  GROUP_LABELS,
  type GroupKey,
} from '@/components/dev/feature-flags/feature-flags-groups';
import { FlagRow } from '@/components/dev/feature-flags/feature-flags-row';
import { type DevFlag } from '@/features/flags-dev/discovery';
import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyP2Bold,
} from '@heidi/design-system/typography';

export const FlagGroup = ({
  groupKey,
  flags,
  isFiltering,
  isExpanded,
  onToggle,
  setRef,
  stickyTopPx,
}: {
  groupKey: GroupKey;
  flags: DevFlag[];
  isFiltering: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  setRef: (el: HTMLDivElement | null) => void;
  stickyTopPx: number;
}) => {
  if (flags.length === 0) {
    // The hydration hint below only makes sense for an unfiltered provider group.
    if (groupKey === 'favourites') return null;
    if (isFiltering) return null;
    return (
      <div
        ref={setRef}
        className="flex flex-col gap-1"
        style={{ scrollMarginTop: stickyTopPx }}
      >
        <TypographyCaptionBold className="text-text-secondary">
          {GROUP_LABELS[groupKey]}
        </TypographyCaptionBold>
        <TypographyCaption className="text-text-tertiary">
          No flags reported. The SDK may still be hydrating.
        </TypographyCaption>
      </div>
    );
  }
  return (
    <div
      ref={setRef}
      className="flex flex-col gap-3"
      style={{ scrollMarginTop: stickyTopPx }}
    >
      {/* Native <button>, not DS Button: a sticky full-width row needs ~14 className
          overrides plus disableAutomaticTypographyClasses, which the DS discourages. */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className="sticky z-10 -mr-2 flex w-full items-center justify-between gap-2 bg-surface-elevated py-1 pr-2 text-left focus-visible:outline-1 focus-visible:outline-outline-focus"
        style={{ top: stickyTopPx }}
        data-testid={`dev-tools-feature-flags-toggle-${groupKey}`}
      >
        <TypographyP2Bold>
          {GROUP_LABELS[groupKey]} ({flags.length})
        </TypographyP2Bold>
        <ChevronDown
          size={14}
          className={`shrink-0 transition-transform ${isExpanded ? '' : '-rotate-90'}`}
          aria-hidden
        />
      </button>
      {isExpanded && (
        <div className="flex flex-col gap-7">
          {flags.map((flag) => (
            <FlagRow
              key={`${flag.provider}:${flag.key}`}
              flag={flag}
              favourite={groupKey === 'favourites'}
            />
          ))}
        </div>
      )}
    </div>
  );
};
